import { get } from '../Services/http';
import { catchError } from './Error.action';
import {
  FACETED_SEARCH_QUERY,
  FACETED_SEARCH_RECEIVE,
  FACETED_SEARCH_ERROR,
  FACETED_SEARCH_TOGGLE_VALUE,
  FACETED_SEARCH_TOGGLE_COMPACT,
  PRODUCT_VIEW_CACHED,
  PRODUCT_FILTERING_RECEIVE,
  PRODUCT_LISTING_RECEIVE,
  PRODUCT_LISTING_LOADING,
} from '../constants';
import { dataLayerFilterSelected, dataLayerFilterDeselected, dataLayerFiltersCleared } from '../Services/Gtm.service';
import { sortCurrentListingWithClerk } from './ProductListing.action';

export const query = (
  queryString = '',
  withHtmlResult = true,
  productsViewCachedId = new Date().getTime() + ''
) => (dispatch, getState) => {
  let url = withHtmlResult
    ? '/api/productFilter/withHtmlResult'
    : '/api/productFilter';
  if (queryString && queryString.trim() !== '') {
    url += `?${queryString}`;
  }

  // The listing shows a skeleton while this is in flight. PRODUCT_LISTING_RECEIVE clears the flag
  // on its own, so only the error path has to reset it.
  dispatch(setProductListingLoading(true));

  return get(url)
    .then((response) => response.json())
    .then((result) => {
      const { productsView, sortCriteria, subNavigation, ...others } = result;
      if (withHtmlResult) {
        window.__litium.cache[PRODUCT_VIEW_CACHED] = {
          productsViewCachedId,
          productsView,
        };
      }
      result = {
        ...others,
        sortCriteria,
        subNavigation,
        productsViewCachedId,
      };

      dispatch(receiveFilters(result));
      dispatch(receiveProductListing(result));
    })
    .catch((ex) => {
      dispatch(setProductListingLoading(false));
      return dispatch(catchError(ex, (error) => searchError(error)));
    });
};

export const setProductListingLoading = (loading) => ({
  type: PRODUCT_LISTING_LOADING,
  payload: loading,
});

const getParamFromQueryString = (queryString, key) => {
  if (!queryString) return null;
  try {
    const parts = queryString.split('&').map((p) => p.split('='));
    const found = parts.find(([k]) => k === key);
    return found ? decodeURIComponent(found[1] || '') : null;
  } catch {
    return null;
  }
};

const submit = (facetFilters, replaceHistory = false) => (
  dispatch,
  getState
) => {
  const filterCriteria = toFilterCriteria(facetFilters);
  const filterIds = facetFilters.map((filter) => filter.id);
  const ignoredParams = ['page'];
  const unChangedParams = (window.location.search.substr(1) || '')
    .split('&')
    .filter((param) => {
      const [id, value] = param.split('=');
      return (
        param.length > 0 &&
        !filterIds.includes(id) &&
        !ignoredParams.includes(id)
      );
    });
  const q = [...unChangedParams, ...filterCriteria].join('&');
  dispatch(saveQuery(q, replaceHistory));
  return dispatch(query(q));
};

export const changeSortBy = (sortBy) => (dispatch, getState) => {
  const allFilters = getState().productFiltering.facetFilters;
  const filterCriteria = toFilterCriteria(allFilters);

  // Preserve existing non-filter params except page and old sortBy
  const ignoredParams = ['page', 'sort_by'];
  const unChangedParams = (window.location.search.substr(1) || '')
    .split('&')
    .filter((param) => {
      const [id] = param.split('=');
      return param.length > 0 && !ignoredParams.includes(id);
    });

  const q = [
    ...unChangedParams,
    ...filterCriteria,
    `sort_by=${encodeURIComponent(sortBy)}`,
  ].join('&');

  dispatch(saveQuery(q));
  // Do not trigger full faceted query; only sort current listing via Clerk
  return dispatch(sortCurrentListingWithClerk(sortBy, q));
};

export const toggleInStock = (checked) => (dispatch, getState) => {
  const ignoredParams = ['page', 'in_stock'];
  const unChangedParams = (window.location.search.substr(1) || '')
    .split('&')
    .filter((param) => {
      const [id] = param.split('=');
      return param.length > 0 && !ignoredParams.includes(id);
    });

  const q = [...unChangedParams, ...(checked ? ['in_stock=true'] : [])].join(
    '&'
  );

  dispatch(saveQuery(q));
  return dispatch(query(q));
};

const toFilterCriteria = (groups) =>
  groups
    .map((group) =>
      group.selectedOptions
        .filter((val) => val && val.length > 0)
        .map(
          (val) => `${encodeURIComponent(group.id)}=${encodeURIComponent(val)}`
        )
    )
    .reduce((flat, current) => [...flat, ...current], []);

/**
 *
 * @param filter : the group has changed facet
 * @param option : changed facet
 */
export const searchFacetChange = (filter = null, option = null) => (
  dispatch,
  getState
) => {
  if (filter) {
    const allFilters = getState().productFiltering.facetFilters;
    const newFilters = updateFilterOption(allFilters, filter, option);
    return dispatch(submit(newFilters));
  }
};

export const updateFilterOption = (
  allFilters = [],
  filter = null,
  option = null
) => {
  if (!filter) return allFilters;
  const filterIndex = allFilters.findIndex((f) => f.id === filter.id);
  const newFilter = option
    ? toggleFilterValue(filter, option)
    : { ...filter, selectedOptions: [] };
  const newFilters = [
    ...allFilters.slice(0, filterIndex),
    newFilter,
    ...allFilters.slice(filterIndex + 1),
  ];
  return newFilters;
};

const toggleFilterValue = (filter, option) => {
  const { singleSelect, selectedOptions } = filter;
  const optionIndex = selectedOptions.indexOf(option.id);
  const newSelectedOptions =
    optionIndex < 0
      ? // not yet selected, select it
        singleSelect
        ? [option.id]
        : [...selectedOptions, option.id]
      : // selected, deSelect  it
        [
          ...selectedOptions.slice(0, optionIndex),
          ...selectedOptions.slice(optionIndex + 1),
        ];


  //tracking
  if (optionIndex < 0) {
    dataLayerFilterSelected(`${filter.id}:${option.label ?? option.id}`);
  } else {
    dataLayerFilterDeselected(`${filter.id}:${option.label ?? option.id}`);
  }

  return { ...filter, selectedOptions: newSelectedOptions };
};

/**
 * @param replaceHistory true for a selection the visitor did not make, so the url is replaced
 * rather than pushed and Back is not left with an entry that looks identical.
 */
export const submitSearchFacet = (allFilters, replaceHistory = false) => (
  dispatch,
  getState
) => {
  dispatch(facetClientChanged(allFilters));
  return dispatch(submit(allFilters, replaceHistory));
};

export const facetClientChanged = (facetFilters) => ({
  type: FACETED_SEARCH_TOGGLE_VALUE,
  payload: {
    facetFilters,
  },
});

export const toggleVisibleDropdownMenu = ({ id }) => ({
  type: FACETED_SEARCH_TOGGLE_COMPACT,
  payload: {
    visibleDropdownMenu: id,
  },
});

export const searchError = (error) => ({
  type: FACETED_SEARCH_ERROR,
  payload: {
    error,
  },
});

export const receiveProductListing = (payload) => {
  return {
    type: PRODUCT_LISTING_RECEIVE,
    payload,
  };
};

export const receiveFilters = (payload) => {
  return {
    type: PRODUCT_FILTERING_RECEIVE,
    payload,
  };
};

export const saveQuery = (queryString, replace = false) => ({
  type: FACETED_SEARCH_QUERY,
  payload: {
    query: queryString,
    replace,
  },
});

/**
 * @param keptFilterIds filters to leave untouched, for filters that are presented by another
 * control than the filter menu and therefore should not be cleared from it. The grade tabs on the
 * Apex product listing are the one case today; with no ids passed every filter is cleared as before.
 */
export const clearFilters = (keptFilterIds = []) => (dispatch, getState) => {
  const allFilters = getState().productFiltering.facetFilters;
  const kept = keptFilterIds.map((id) => id.toLowerCase());

  const clearedFilters = allFilters.map((filter) =>
    kept.includes(filter.id?.toLowerCase())
      ? filter
      : {
          ...filter,
          selectedOptions: [],
        }
  );

  dispatch(facetClientChanged(clearedFilters));

  return dispatch(submit(clearedFilters));
};

export const clearFilter = (filterId, optionId) => (dispatch, getState) => {
  const allFilters = getState().productFiltering.facetFilters;

  const filterToUpdate = allFilters.find((filter) => filter.id === filterId);
  if (!filterToUpdate) return;

  const updatedFilter = {
    ...filterToUpdate,
    selectedOptions: filterToUpdate.selectedOptions.filter(
      (selectedOption) => selectedOption !== optionId
    ),
  };

  const updatedFilters = allFilters.map((filter) =>
    filter.id === filterId ? updatedFilter : filter
  );

  //tracking
  dataLayerFiltersCleared();

  dispatch(facetClientChanged(updatedFilters));
  return dispatch(submit(updatedFilters));
};
